import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import axios from 'axios';
import { FileText, User, Plus } from 'lucide-react';

// The CV sections, in the order they appear on the public /cv page.
export const CV_SECTIONS = [
  'Education',
  'Selected Solo Exhibitions',
  'Selected Group Exhibitions',
  'Residencies & Fellowships',
  'Talks',
  'Teaching & Facilitation',
  'Collections',
  'Selected Bibliography',
  'Press & Publications',
];

// Shared dark-theme admin UI styles (site palette: #252525 / #b7a78b)
const ui = {
  card: 'bg-[#1d1d1d] rounded-xl border border-[#484540] p-6 md:p-8 mb-8 shadow-lg',
  title: 'text-xl font-semibold mb-1 capitalize text-[#e7ddd3] tracking-wide flex items-center gap-2',
  subtitle: 'text-sm text-[#8a8175] mb-6',
  label: 'block text-xs font-medium text-[#8a8175] uppercase tracking-wide mb-1.5',
  input:
    'w-full bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] placeholder-[#6f675c] focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition',
  select:
    'w-full bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition cursor-pointer',
  // Entries can run over several lines — press Enter inside the box for a line break.
  textarea:
    'w-full bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] placeholder-[#6f675c] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition',
  primaryBtn:
    'inline-flex items-center justify-center gap-2 bg-[#b7a78b] hover:bg-[#d0c3a6] text-[#252525] text-sm font-semibold px-8 py-3 rounded-lg uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  deleteBtn:
    'bg-red-600/90 hover:bg-red-600 text-white text-xs font-semibold uppercase px-4 py-2 rounded-md transition-colors',
  editBtn:
    'bg-[#252525] border border-[#484540] hover:border-[#b7a78b] text-[#e4e4e3] text-xs font-semibold uppercase px-4 py-2 rounded-md transition-colors',
};

const emptyEntry = { section: CV_SECTIONS[0], year: '', text: '', order: 0 };

// Surface the real reason a save failed — a generic "Failed" alert hides whether
// the API is down, the database is unreachable, or the request itself was bad.
const describeError = (error) => {
  if (error.response) {
    const data = error.response.data || {};
    return data.error || data.message || `Server responded ${error.response.status}`;
  }
  if (error.request) return 'No response from the API — is the server running?';
  return error.message;
};

const Cv = () => {
  const [meta, setMeta] = useState({
    name: '', born: '', resides: '', email: '', website: '', instagram: '',
  });
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyEntry);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);

  // inline edit of an existing entry
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(emptyEntry);

  useEffect(() => {
    fetchCv();
  }, []);

  const fetchCv = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/cv`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.meta) {
        setMeta({
          name: data.meta.name || '',
          born: data.meta.born || '',
          resides: data.meta.resides || '',
          email: data.meta.email || '',
          website: data.meta.website || '',
          instagram: data.meta.instagram || '',
        });
      }
      setEntries(data.entries || []);
    } catch (error) {
      console.error('Error fetching CV:', error);
    }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/cv/meta`, meta);
      alert('CV details saved.');
      fetchCv();
    } catch (error) {
      alert(`Failed to save CV details.\n\n${describeError(error)}`);
    } finally {
      setSavingMeta(false);
    }
  };

  const addEntry = async () => {
    if (!form.text.trim()) {
      alert('Please enter the entry text.');
      return;
    }
    setSavingEntry(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/cv/entry`, form);
      setForm({ ...emptyEntry, section: form.section }); // keep the section selected
      fetchCv();
    } catch (error) {
      alert(`Failed to add entry.\n\n${describeError(error)}`);
    } finally {
      setSavingEntry(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setEdit({
      section: entry.section,
      year: entry.year || '',
      text: entry.text,
      order: entry.order ?? 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdit(emptyEntry);
  };

  const saveEdit = async (id) => {
    if (!edit.text.trim()) {
      alert('Please enter the entry text.');
      return;
    }
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/cv/entry/${id}`, edit);
      cancelEdit();
      fetchCv();
    } catch (error) {
      alert(`Failed to update entry.\n\n${describeError(error)}`);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this CV entry?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/cv/entry/${id}`);
      setEntries((prev) => prev.filter((e) => e._id !== id));
    } catch (error) {
      alert(`Failed to delete entry.\n\n${describeError(error)}`);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#252525]" style={{ fontFamily: 'Muli' }}>
      <Sidebar />
      <div className="flex-1 bg-[#252525] min-h-screen pb-16">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#484540] bg-[#1d1d1d] px-6 py-4">
          <p className="text-sm font-medium text-[#8a8175]">Admin Dashboard</p>
          <span className="text-sm font-semibold text-[#e7ddd3]">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-[#e7ddd3]">CV</h1>
            <p className="mt-1 text-sm text-[#8a8175]">Manage everything shown on the public /cv page.</p>
          </div>

          {/* ── HEADER & CONTACT ─────────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><User className="h-5 w-5 text-[#b7a78b]" /> Header &amp; Contact</h2>
            <p className={ui.subtitle}>The name, intro lines and contact block at the top and bottom of the CV.</p>

            <div className="grid gap-4 sm:grid-cols-2 max-w-3xl">
              <div className="sm:col-span-2">
                <label className={ui.label}>Name</label>
                <input className={ui.input} type="text" placeholder="Sope Adelaja"
                  value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Born</label>
                <input className={ui.input} type="text" placeholder="Born in 2000 in Lagos, Nigeria"
                  value={meta.born} onChange={(e) => setMeta({ ...meta, born: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Resides</label>
                <input className={ui.input} type="text" placeholder="Resides and works in Lagos, Nigeria"
                  value={meta.resides} onChange={(e) => setMeta({ ...meta, resides: e.target.value })} />
              </div>
              <div>
                <label className={ui.label}>Email</label>
                <input className={ui.input} type="text" placeholder="hello@sopeadelaja.com"
                  value={meta.email} onChange={(e) => setMeta({ ...meta, email: e.target.value })} />
              </div>
              <div>
                <label className={ui.label}>Website</label>
                <input className={ui.input} type="text" placeholder="www.sopeadelaja.com"
                  value={meta.website} onChange={(e) => setMeta({ ...meta, website: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={ui.label}>Instagram</label>
                <input className={ui.input} type="text" placeholder="@sopeadelaja"
                  value={meta.instagram} onChange={(e) => setMeta({ ...meta, instagram: e.target.value })} />
              </div>
            </div>

            <button className={`${ui.primaryBtn} mt-6`} onClick={saveMeta} disabled={savingMeta}>
              {savingMeta ? 'Saving…' : 'Save Details'}
            </button>
          </div>

          {/* ── ADD ENTRY ────────────────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><Plus className="h-5 w-5 text-[#b7a78b]" /> Add CV Entry</h2>
            <p className={ui.subtitle}>Pick a section, add the year (optional) and the entry text. Press Enter inside the entry box to start a new line.</p>

            <div className="grid gap-4 sm:grid-cols-4 max-w-4xl">
              <div className="sm:col-span-2">
                <label className={ui.label}>Section</label>
                <select className={ui.select} value={form.section}
                  onChange={(e) => setForm({ ...form, section: e.target.value })}>
                  {CV_SECTIONS.map((sec) => <option key={sec} value={sec}>{sec}</option>)}
                </select>
              </div>
              <div>
                <label className={ui.label}>Year (optional)</label>
                <input className={ui.input} type="text" placeholder="2026"
                  value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              </div>
              <div>
                <label className={ui.label}>Order</label>
                <input className={ui.input} type="number" placeholder="0"
                  value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
              </div>
              <div className="sm:col-span-4">
                <label className={ui.label}>Entry</label>
                <textarea className={ui.textarea} rows={3}
                  placeholder="In Her Hands, Red Door Gallery, Lagos, Nigeria"
                  value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
              </div>
            </div>

            <button className={`${ui.primaryBtn} mt-6`} onClick={addEntry} disabled={savingEntry}>
              {savingEntry ? 'Adding…' : 'Add Entry'}
            </button>
          </div>

          {/* ── ENTRIES BY SECTION ───────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><FileText className="h-5 w-5 text-[#b7a78b]" /> CV Entries</h2>
            <p className={ui.subtitle}>Everything currently on the CV, grouped by section.</p>

            {entries.length === 0 ? (
              <p className="text-sm text-[#6f675c]">No CV entries yet.</p>
            ) : (
              <div className="space-y-8">
                {CV_SECTIONS.filter((sec) => entries.some((e) => e.section === sec)).map((sec) => (
                  <div key={sec}>
                    <p className="text-[#b7a78b] text-sm font-semibold uppercase tracking-wide mb-3">
                      {sec} <span className="text-[#6f675c]">({entries.filter((e) => e.section === sec).length})</span>
                    </p>

                    <div className="space-y-3">
                      {entries.filter((e) => e.section === sec).map((entry) => {
                        const isEditing = editingId === entry._id;
                        return (
                          <div key={entry._id} className="rounded-xl border border-[#484540] bg-[#252525] p-4">
                            {isEditing ? (
                              <div className="space-y-3">
                                <div className="grid gap-3 sm:grid-cols-4">
                                  <div className="sm:col-span-2">
                                    <label className={ui.label}>Section</label>
                                    <select className={ui.select} value={edit.section}
                                      onChange={(e) => setEdit({ ...edit, section: e.target.value })}>
                                      {CV_SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className={ui.label}>Year</label>
                                    <input className={ui.input} type="text" value={edit.year}
                                      onChange={(e) => setEdit({ ...edit, year: e.target.value })} />
                                  </div>
                                  <div>
                                    <label className={ui.label}>Order</label>
                                    <input className={ui.input} type="number" value={edit.order}
                                      onChange={(e) => setEdit({ ...edit, order: e.target.value })} />
                                  </div>
                                </div>
                                <textarea className={ui.textarea} rows={3} value={edit.text}
                                  onChange={(e) => setEdit({ ...edit, text: e.target.value })} />
                                <div className="flex gap-2">
                                  <button className={ui.primaryBtn} onClick={() => saveEdit(entry._id)}>Save</button>
                                  <button className={ui.editBtn} onClick={cancelEdit}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="min-w-0 flex-1">
                                  <p className="text-[15px] text-[#cdc2ad] leading-relaxed whitespace-pre-line">
                                    {entry.year && <span className="text-[#8a8175] mr-2">{entry.year}</span>}
                                    {entry.text}
                                  </p>
                                </div>
                                <div className="flex shrink-0 gap-2">
                                  <button className={ui.editBtn} onClick={() => startEdit(entry)}>Edit</button>
                                  <button className={ui.deleteBtn} onClick={() => deleteEntry(entry._id)}>Delete</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cv;
